import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Inbox as InboxIcon, Check, CheckCheck, AlertTriangle, Bell, UserPlus, Briefcase, Receipt, FileText, Zap, Mail, ListChecks } from "lucide-react";
import { api } from "@/lib/api";
import { useOrg } from "@/context/OrgContext";
import { PageHeader, Scroller, Spinner, EmptyState } from "@/pages/_shared";

const CAT_ICON = { lead: UserPlus, job: Briefcase, invoice: Receipt, quote: FileText, automation: Zap, email: Mail, task: ListChecks, system: Bell };
const SEV = {
  urgent: { ring: "border-red-500/40", dot: "bg-red-400", text: "text-red-300" },
  warning: { ring: "border-gold/40", dot: "bg-gold", text: "text-gold" },
  info: { ring: "border-white/10", dot: "bg-white/40", text: "text-white/50" },
};

export default function Inbox() {
  const { isGroupView, activeCompany, activeCompanyId, companies } = useOrg();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("all");
  const nav = useNavigate();
  const companyName = (id) => companies.find((c) => c.id === id)?.name;

  const load = useCallback(async () => {
    const { data } = await api.get("/notifications", { params: { limit: 200 } });
    setData(data);
  }, []);
  useEffect(() => { load(); }, [load, activeCompanyId]);

  const markRead = async (n) => { await api.post(`/notifications/${n.id}/read`); load(); };
  const done = async (n) => { await api.post(`/notifications/${n.id}/done`); load(); };
  const readAll = async () => { await api.post("/notifications/read-all"); load(); };

  if (!data) return <Spinner />;
  const items = data.notifications.filter((n) => tab === "all" || (tab === "unread" ? !n.read : n.severity === tab));

  return (
    <Scroller>
      <PageHeader title="Attention Inbox" subtitle={isGroupView ? "Everything that needs you, across all businesses" : `What needs attention at ${activeCompany?.name || "this business"}`} icon={InboxIcon}
        action={<button onClick={readAll} className="flex items-center gap-2 rounded-xl border border-white/10 px-3.5 py-2 text-sm text-white/70 hover:border-gold/25 hover:text-white" data-testid="inbox-read-all"><CheckCheck className="h-4 w-4" /> Mark all read</button>} />

      <div className="mb-5 flex flex-wrap gap-2" data-testid="inbox-tabs">
        {["all", "unread", "urgent", "warning", "info"].map((t) => (
          <button key={t} onClick={() => setTab(t)} className={`rounded-full border px-3 py-1 text-xs capitalize transition-colors ${tab === t ? "border-gold/40 bg-gold/10 text-gold" : "border-white/10 text-white/50 hover:text-white"}`} data-testid={`inbox-tab-${t}`}>
            {t}{t === "unread" && data.unread ? ` (${data.unread})` : ""}
          </button>
        ))}
      </div>

      {items.length === 0 ? (
        <EmptyState icon={Bell} title="You're all caught up" hint="New leads, jobs, invoices and overdue alerts will appear here automatically." />
      ) : (
        <div className="space-y-2">
          {items.map((n) => {
            const sev = SEV[n.severity] || SEV.info;
            const Icon = CAT_ICON[n.category] || Bell;
            const isTask = (n.entity || {}).kind === "task";
            return (
              <motion.div key={n.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                className={`flex items-start gap-3 rounded-xl border bg-charcoal px-4 py-3 ${sev.ring} ${n.read ? "opacity-55" : ""}`} data-testid={`notif-${n.id}`}>
                <div className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white/5 ${sev.text}`}><Icon className="h-4 w-4" /></div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    {!n.read && <span className={`h-1.5 w-1.5 rounded-full ${sev.dot}`} />}
                    <p className="truncate text-sm font-medium text-white/90">{n.title}</p>
                  </div>
                  <p className="mt-0.5 text-xs text-white/50">{n.body}</p>
                  <div className="mt-1 flex items-center gap-2 text-[10px] uppercase tracking-wider text-white/25">
                    {isGroupView && n.company_id && <span className="text-gold/50">{companyName(n.company_id) || "business"}</span>}
                    <span>{new Date(n.created_at).toLocaleString()}</span>
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  {isTask ? (
                    <button onClick={() => done(n)} className="flex items-center gap-1 rounded-lg border border-white/10 px-2.5 py-1 text-xs text-emerald-300 hover:border-emerald-400/40" data-testid={`notif-done-${n.id}`}><Check className="h-3.5 w-3.5" /> Done</button>
                  ) : !n.read && (
                    <button onClick={() => markRead(n)} className="rounded-lg border border-white/10 px-2.5 py-1 text-xs text-white/60 hover:border-gold/30 hover:text-white" data-testid={`notif-read-${n.id}`}>Mark read</button>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </Scroller>
  );
}
